/**
 * Adyen card frame (PD-17168).
 *
 * Adyen loads its secured card fields only for a page whose address is on
 * the client key's allowed-origins list, and a LIVE allowed origin must be
 * https. The storefront mobile app on iOS runs its pages from
 * `capacitor://<storefront host>`, which can never be on that list, so the
 * fields refuse to load there with "Invalid Origin".
 *
 * The fix is a small card-entry page served from the storefront's real
 * https address and framed inside the app. Adyen sees the https page as the
 * origin, mounts its fields inside it, and the page forwards every
 * secured-field event to the app over postMessage. The app applies those
 * events exactly as it would have applied Adyen's own callbacks, so saving a
 * card works the same either way. Only encrypted card data ever crosses the
 * frame boundary; the card number never leaves Adyen's own iframes.
 *
 * This file holds the message protocol both sides share, and the pure
 * checks that decide who may talk to whom.
 */

/** Path of the card-entry page on the storefront (built by ecommerce-web). */
export const ADYEN_CARD_FRAME_PATH = '/adyen-card-frame.html';

/**
 * Stamped on every message so unrelated postMessage traffic is ignored, and
 * so a future breaking change can be told apart: an installed app and the
 * deployed card-entry page are released separately and may differ in age.
 */
export const ADYEN_CARD_FRAME_PROTOCOL = 'workd-adyen-card-frame/1';

/**
 * Whether Adyen can accept a page with this location protocol as an
 * allowed origin. Only web addresses can be listed; an app-only scheme such
 * as `capacitor:` cannot, so a page running under one needs the frame.
 */
export function isAdyenAllowedOriginProtocol(protocol: string): boolean {
	return protocol === 'https:' || protocol === 'http:';
}

/**
 * Build the card-entry page URL from the storefront's web address.
 * Returns null when the address is missing or not an https/http URL, so the
 * caller can fall back instead of framing something unexpected.
 */
export function getAdyenCardFrameUrl(
	storefrontUrl: string | null | undefined,
): string | null {
	if (storefrontUrl == null || storefrontUrl.trim() === '') {
		return null;
	}

	let parsed: URL;

	try {
		parsed = new URL(storefrontUrl.trim());
	} catch {
		return null;
	}

	if (!isAdyenAllowedOriginProtocol(parsed.protocol)) {
		return null;
	}

	return `${parsed.origin}${ADYEN_CARD_FRAME_PATH}`;
}

/**
 * Whether the card-entry page may accept setup from, and send card data to,
 * a parent at this origin.
 *
 * Only the storefront app for this very storefront qualifies: an app-scheme
 * page (`capacitor://`) whose host is the card-entry page's own host. The
 * mobile build sets the app's in-app host to the storefront host, so a
 * genuine app always matches, while a web page elsewhere that frames the
 * card-entry page cannot receive anything from it.
 */
export function isTrustedAdyenCardFrameParent(
	parentOrigin: string,
	frameHost: string,
): boolean {
	if (parentOrigin === '' || parentOrigin === 'null' || frameHost === '') {
		return false;
	}

	let parsed: URL;

	try {
		parsed = new URL(parentOrigin);
	} catch {
		return false;
	}

	return (
		parsed.protocol === 'capacitor:' &&
		parsed.host.toLowerCase() === frameHost.toLowerCase()
	);
}

// ============================================================================
// Messages
// ============================================================================

/**
 * One secured-field callback from Adyen, as forwarded by the card-entry
 * page. Mirrors the four callbacks AdyenFrontendService listens to, so the
 * app applies them with the same code path as a locally mounted card.
 */
export type AdyenSecuredFieldEventModel =
	| {
			kind: 'change';
			isValid: boolean;
			paymentMethod: Record<string, string> | null;
	  }
	| { kind: 'brand'; brand: string | null }
	| { kind: 'fieldValid'; fieldType: string | null; endDigits: string | null }
	| { kind: 'binValue'; binValue: string | null };

/**
 * Colors and fonts the card-entry page paints its labels and field boxes
 * with, resolved by the app from its own themed form so the framed fields
 * match the fields around them.
 */
export interface AdyenCardFrameStylesModel {
	/**
	 * The color behind the frame. Painted by the page itself so the frame
	 * never shows the browser's default canvas (a white box on a dark form).
	 */
	pageBackground: string;
	fontFamily: string;
	labelColor: string;
	labelFontSize: string;
	labelFontWeight: string;
	requiredColor: string;
	fieldBackground: string;
	fieldBorderColor: string;
	fieldBorderWidth: string;
	fieldBorderRadius: string;
	fieldBoxShadow: string;
	fieldHeight: string;
	fieldPaddingX: string;
	fieldFontSize: string;
	fieldFontWeight: string;
	focusColor: string;
	textColor: string;
	placeholderColor: string;
	errorColor: string;
}

/** Translated label text; the card-entry page has no translation layer. */
export interface AdyenCardFrameLabelsModel {
	cardNumber: string;
	expiryDate: string;
	securityCode: string;
}

/** Same values as PaymentGatewayConfig['environment']. */
export type AdyenCardFrameEnvironment = 'sandbox' | 'production';

/** Translated hint text shown inside the empty secured fields. */
export interface AdyenCardFramePlaceholdersModel {
	cardNumber: string;
	expiryDate: string;
	securityCode: string;
}

export interface AdyenCardFrameInitMessageModel {
	protocol: typeof ADYEN_CARD_FRAME_PROTOCOL;
	type: 'init';
	clientKey: string;
	environment: AdyenCardFrameEnvironment;
	styles: AdyenCardFrameStylesModel;
	labels: AdyenCardFrameLabelsModel;
	placeholders: AdyenCardFramePlaceholdersModel;
}

export type AdyenCardFrameToParentMessageModel =
	| { protocol: typeof ADYEN_CARD_FRAME_PROTOCOL; type: 'mounted' }
	| { protocol: typeof ADYEN_CARD_FRAME_PROTOCOL; type: 'height'; height: number }
	| {
			protocol: typeof ADYEN_CARD_FRAME_PROTOCOL;
			type: 'event';
			event: AdyenSecuredFieldEventModel;
	  }
	| { protocol: typeof ADYEN_CARD_FRAME_PROTOCOL; type: 'error'; message: string };

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value != null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
	return typeof value === 'string' ? value : null;
}

function isStringRecord(value: unknown): value is Record<string, string> {
	return (
		isRecord(value) &&
		Object.values(value).every((entry): boolean => typeof entry === 'string')
	);
}

const STYLE_KEYS: Array<keyof AdyenCardFrameStylesModel> = [
	'pageBackground',
	'fontFamily',
	'labelColor',
	'labelFontSize',
	'labelFontWeight',
	'requiredColor',
	'fieldBackground',
	'fieldBorderColor',
	'fieldBorderWidth',
	'fieldBorderRadius',
	'fieldBoxShadow',
	'fieldHeight',
	'fieldPaddingX',
	'fieldFontSize',
	'fieldFontWeight',
	'focusColor',
	'textColor',
	'placeholderColor',
	'errorColor',
];

function parseSecuredFieldEvent(
	value: unknown,
): AdyenSecuredFieldEventModel | null {
	if (!isRecord(value)) {
		return null;
	}

	switch (value.kind) {
		case 'change':
			if (typeof value.isValid !== 'boolean') {
				return null;
			}

			return {
				kind: 'change',
				isValid: value.isValid,
				paymentMethod: isStringRecord(value.paymentMethod)
					? value.paymentMethod
					: null,
			};
		case 'brand':
			return { kind: 'brand', brand: stringOrNull(value.brand) };
		case 'fieldValid':
			return {
				kind: 'fieldValid',
				fieldType: stringOrNull(value.fieldType),
				endDigits: stringOrNull(value.endDigits),
			};
		case 'binValue':
			return { kind: 'binValue', binValue: stringOrNull(value.binValue) };
		default:
			return null;
	}
}

/**
 * Validate a message the app received from the card-entry page. Returns
 * null for anything that is not a well-formed message of this protocol.
 */
export function parseAdyenCardFrameToParentMessage(
	data: unknown,
): AdyenCardFrameToParentMessageModel | null {
	if (!isRecord(data) || data.protocol !== ADYEN_CARD_FRAME_PROTOCOL) {
		return null;
	}

	switch (data.type) {
		case 'mounted':
			return { protocol: ADYEN_CARD_FRAME_PROTOCOL, type: 'mounted' };
		case 'height':
			if (
				typeof data.height !== 'number' ||
				!Number.isFinite(data.height) ||
				data.height < 0
			) {
				return null;
			}

			return {
				protocol: ADYEN_CARD_FRAME_PROTOCOL,
				type: 'height',
				height: data.height,
			};
		case 'event': {
			const event = parseSecuredFieldEvent(data.event);

			return event != null
				? { protocol: ADYEN_CARD_FRAME_PROTOCOL, type: 'event', event }
				: null;
		}
		case 'error':
			return typeof data.message === 'string'
				? {
						protocol: ADYEN_CARD_FRAME_PROTOCOL,
						type: 'error',
						message: data.message,
					}
				: null;
		default:
			return null;
	}
}

/**
 * Validate the setup message the card-entry page received from the app.
 * Returns null for anything malformed.
 */
export function parseAdyenCardFrameInitMessage(
	data: unknown,
): AdyenCardFrameInitMessageModel | null {
	if (
		!isRecord(data) ||
		data.protocol !== ADYEN_CARD_FRAME_PROTOCOL ||
		data.type !== 'init' ||
		typeof data.clientKey !== 'string' ||
		data.clientKey === '' ||
		(data.environment !== 'sandbox' && data.environment !== 'production') ||
		!isRecord(data.styles) ||
		!isRecord(data.labels) ||
		!isRecord(data.placeholders)
	) {
		return null;
	}

	const labels = data.labels;
	const placeholders = data.placeholders;
	const styles: Partial<AdyenCardFrameStylesModel> = {};

	for (const key of STYLE_KEYS) {
		const value = data.styles[key];

		if (typeof value !== 'string') {
			return null;
		}

		styles[key] = value;
	}

	if (
		typeof labels.cardNumber !== 'string' ||
		typeof labels.expiryDate !== 'string' ||
		typeof labels.securityCode !== 'string' ||
		typeof placeholders.cardNumber !== 'string' ||
		typeof placeholders.expiryDate !== 'string' ||
		typeof placeholders.securityCode !== 'string'
	) {
		return null;
	}

	return {
		protocol: ADYEN_CARD_FRAME_PROTOCOL,
		type: 'init',
		clientKey: data.clientKey,
		environment: data.environment,
		styles: styles as AdyenCardFrameStylesModel,
		labels: {
			cardNumber: labels.cardNumber,
			expiryDate: labels.expiryDate,
			securityCode: labels.securityCode,
		},
		placeholders: {
			cardNumber: placeholders.cardNumber,
			expiryDate: placeholders.expiryDate,
			securityCode: placeholders.securityCode,
		},
	};
}
